import { BalanceData, CheckinRewardData, Rarity } from "../data/balance";
import { ChestResult } from "../sim/chest";
import { chestOfRarity, chestShare, metaSeed, utcDay } from "./orders";
import { Wallet } from "./wallet";

/**
 * The check-in calendar (ENGAGEMENT_2026-09-23 §B5, orders.json `checkin`): 28 slots since MP-24 (seven before), one
 * claimed on each UTC day the app is opened. Missed days never reset it and there is no streak; after the last slot it
 * starts over. Coming back after `welcomeBackAfterDays` or more days away adds a welcome-back bundle to that day's
 * check-in. A save written with a different number of slots keeps its place in the cycle (`fromJson`).
 */

/** The cycle length a save from before the saved `slots` field was written with (the 7-slot calendar). */
const LEGACY_SLOTS = 7;

/** One check-in's pay, resolved at the player's level. */
export interface CheckinPay {
  readonly gold: number;
  readonly speedUpMinutes: number;
  readonly chest: ChestResult | null;
  /** Gems (D20: the weekly milestones; MP-08: day 1), paid into the wallet by the claim. */
  readonly gems: number;
  /** Fusion Sigils of a rarity (MP-24: day 14), banked by the caller into the profile's forge; null for none. */
  readonly sigil: Rarity | null;
  readonly sigils: number;
}

export interface CheckinClaim {
  /** 0-based slot claimed. */
  readonly slot: number;
  readonly pay: CheckinPay;
  /** The welcome-back bundle, when this check-in came after a long absence. */
  readonly welcome: CheckinPay | null;
}

/** `offset`: the claim count the current cycle is counted from; `slots`: the cycle length it was saved with. */
export interface CheckinJson { claimed: number; lastClaimDay: number; lastSeenDay: number; shownDay: number; welcomePending: boolean; offset: number; slots: number }

export class CheckIn {
  /** Check-ins claimed ever (the chests' seeds count with it); the next slot is `(claimed − offset) mod slots`. */
  claimed = 0;
  /** Where the cycle is counted from: 0 for a new save, moved by a save migrated from another cycle length. */
  offset = 0;
  lastClaimDay = -1;
  /** The last UTC day the app was opened (−1: never). */
  lastSeenDay = -1;
  /** The last day the card came up by itself (once a day). */
  shownDay = -1;
  welcomePending = false;
  /** Where check-in gems go (D20); set by the app (LevelCatalog), not saved here. Null: no gems. */
  wallet: Wallet | null = null;

  constructor(readonly balance: BalanceData, readonly playerId: string) {}

  get slots(): readonly CheckinRewardData[] { return this.balance.orders.checkinSlots; }
  /** The slot the next check-in claims (0-based). */
  get slot(): number { return this.slots.length > 0 ? mod(this.claimed - this.offset, this.slots.length) : 0; }
  /** Whether at least one slot of the current cycle is claimed (slot 0 next: the last cycle ended, or none started). */
  get cycleStarted(): boolean { return this.claimed > this.offset; }

  /** The app was opened: notes a long absence (for the welcome-back bundle) and the day. */
  visit(now: number): void {
    const today = utcDay(now);
    if (this.lastSeenDay >= 0 && today - this.lastSeenDay >= this.balance.orders.welcomeBackAfterDays) this.welcomePending = true;
    this.lastSeenDay = Math.max(this.lastSeenDay, today);
  }

  canClaim(now: number): boolean { return this.slots.length > 0 && utcDay(now) > this.lastClaimDay; }
  /** The card shows by itself on the first hub visit of a day with a check-in to claim. */
  shouldShow(now: number): boolean { return this.canClaim(now) && utcDay(now) > this.shownDay; }
  markShown(now: number): void { this.shownDay = Math.max(this.shownDay, utcDay(now)); }

  /** What a slot pays at `level` (the chest is rolled with the seed the claim would use). */
  payFor(data: CheckinRewardData, level: number, seedIndex = this.claimed): CheckinPay {
    const L = Math.max(1, level);
    return {
      gold: chestShare(this.balance, L, data.resources),
      speedUpMinutes: data.speedUpMinutes,
      chest: data.chestRarity === null ? null : chestOfRarity(this.balance, L, data.chestRarity, metaSeed(this.playerId, "checkin", seedIndex)),
      gems: data.gems,
      sigil: data.sigil,
      sigils: data.sigils,
    };
  }

  /** Claims today's check-in (and the welcome-back bundle when one is due); null when today's is already taken. */
  claim(now: number, level: number): CheckinClaim | null {
    if (!this.canClaim(now)) return null;
    const slot = this.slot;
    const pay = this.payFor(this.slots[slot], level);
    const welcome = this.welcomePending ? this.payFor(this.balance.orders.welcomeBack, level) : null;
    this.claimed++;
    this.lastClaimDay = utcDay(now);
    this.welcomePending = false;
    this.wallet?.grantGems(pay.gems + (welcome?.gems ?? 0), "checkin", now, `slot${slot + 1}`);
    return { slot, pay, welcome };
  }

  toJson(): CheckinJson {
    return { claimed: this.claimed, lastClaimDay: this.lastClaimDay, lastSeenDay: this.lastSeenDay, shownDay: this.shownDay, welcomePending: this.welcomePending, offset: this.offset, slots: this.slots.length };
  }

  static fromJson(balance: BalanceData, playerId: string, j: unknown): CheckIn {
    const c = new CheckIn(balance, playerId);
    if (typeof j !== "object" || j === null) return c;
    const src = j as Partial<CheckinJson>;
    for (const k of ["claimed", "lastClaimDay", "lastSeenDay", "shownDay"] as const) if (Number.isInteger(src[k])) c[k] = src[k]!;
    if (typeof src.welcomePending === "boolean") c.welcomePending = src.welcomePending;
    if (Number.isInteger(src.offset)) c.offset = src.offset!;
    // A save from another cycle length (the 7-slot calendar wrote no `slots`) keeps its place: slot n stays slot n.
    const was = Number.isInteger(src.slots) && src.slots! > 0 ? src.slots! : LEGACY_SLOTS, now = c.slots.length;
    if (now > 0 && was !== now) c.offset = c.claimed - Math.min(mod(c.claimed - c.offset, was), now - 1);
    return c;
  }
}

/** A non-negative remainder (a migrated offset may sit above the claims). */
function mod(a: number, n: number): number { return ((a % n) + n) % n; }
